import { DeleteOutlined } from '@ant-design/icons';
import { Button, Col, Empty, Form, Input, message, Row, Select, Space, Spin, Checkbox, Radio, Divider, Tooltip } from 'antd';
import debounce from 'lodash/debounce';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useDiagnostics from './hooks/useDiagnostics';
import DiagnosticSelector from './DiagnosticSelector';

export const DiagnosticCard = ({
  field,
  index,
  onRemove,
  recommendatiosModal,
  diagnosticField,
  showDiagnosticTypification = false,
  diagnosticTypificationOptions = null,
  t,
  form,
  showIndications = true,
  showSuggestions = false,
}) => {
  const fieldPath = Array.isArray(diagnosticField) ? diagnosticField.join('-') : diagnosticField;
  const baseId = `${fieldPath}-${field.name}`;
  return (
    <Row gutter={[20, 20]} className="mt-20 diagnostic-container p-10" key={field.key}>
      <Col lg={24}>
        <Form.Item name={[field.name, 'id']} hidden>
          <Input id={`${baseId}-id`} />
        </Form.Item>
        <Form.Item name={[field.name, 'diagnosticable_id']} hidden>
          <Input id={`${baseId}-diagnosticable-id`} />
        </Form.Item>
        <Form.Item name={[field.name, 'diagnosticable_type']} hidden>
          <Input id={`${baseId}-diagnosticable-type`} />
        </Form.Item>
        <Row gutter={[20, 20]} className="mt-20">
          {showDiagnosticTypification && diagnosticTypificationOptions && (
            <Col lg={2}>
              <Form.Item name={[field.name, 'pathological']} valuePropName="checked" noStyle>
                <Checkbox id={`${baseId}-pathological`} />
              </Form.Item>
            </Col>
          )}
          <Col lg={showDiagnosticTypification ? 2 : 4}>
            <Form.Item name={[field.name, 'code']} noStyle>
              <Input id={`${baseId}-code`} bordered={false} className="pl-0" readOnly />
            </Form.Item>
          </Col>
          <Col lg={showDiagnosticTypification ? 12 : 20}>
            <Form.Item name={[field.name, 'description']} noStyle>
              <Input id={`${baseId}-description`} bordered={false} className="pl-0" readOnly />
            </Form.Item>
          </Col>
          {showDiagnosticTypification && diagnosticTypificationOptions && (
            <Col lg={8}>
              <Row gutter={10}>
                <Col>
                  <Form.Item name={[field.name, 'classification']} noStyle>
                    <Select
                      id={`${baseId}-classification`}
                      placeholder={diagnosticTypificationOptions.classification?.label || 'Clasificación'}
                      size="small"
                      style={{ width: 120 }}
                    >
                      {diagnosticTypificationOptions.classification?.options?.map((option) => (
                        <Select.Option key={option.value} value={option.value}>
                          {option.shortLabel || option.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item name={[field.name, 'occupational']} noStyle>
                    <Radio.Group id={`${baseId}-occupational`} size="small">
                      {diagnosticTypificationOptions.occupational?.options?.map((option) => (
                        <Tooltip key={option.value} title={option.label} overlayClassName="diagnostic-option-tooltip">
                          <Radio value={option.value}>{option.shortLabel || option.label}</Radio>
                        </Tooltip>
                      ))}
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          )}
        </Row>
        {(showIndications || showSuggestions) && <Divider className="diagnostic-divider" />}
        <Row className={(showIndications || showSuggestions) ? "mb-20" : ""} justify="end">
          <Col>
            <Space>
              {showIndications && (
                <Button
                  id="indicationsButton"
                  onClick={() =>
                    recommendatiosModal.showModal({
                      type: 'indication',
                      from: 'diagnostics',
                      diagnosticKey: field.name,
                      diagnosticField,
                    })
                  }
                  className="cmt-button secondary"
                  type="primary"
                  size="small"
                >
                  {t('medicalConsultations.healthForm.indications')}
                </Button>
              )}
              {showSuggestions && (
                <Button
                  id="suggestionsButton"
                  onClick={() =>
                    recommendatiosModal.showModal({
                      type: 'suggestion',
                      from: 'diagnostics',
                      diagnosticKey: field.name,
                      diagnosticField,
                    })
                  }
                  className="cmt-button secondary"
                  type="primary"
                  size="small"
                >
                  {t('medicalConsultations.healthForm.suggestions')}
                </Button>
              )}
              <Button
                icon={<DeleteOutlined />}
                size="large"
                type="text"
                onClick={() => onRemove(field.name)}
                title={'Eliminar diagnóstico'}
                className="delete-diagnostic-button"
              />
            </Space>
          </Col>
        </Row>
        {showIndications && (
          <Row>
            <Col lg={24}>
              <strong>Recomendaciones</strong>
              <Form.Item name={[field.name, 'indication']} initialValue={form.getFieldValue(diagnosticField)?.[field.name]?.recommendations} noStyle>
                <Input.TextArea placeholder={t('medicalConsultations.healthForm.indications')} bordered={false} autoSize={{ minRows: 1, maxRows: 5 }} />
              </Form.Item>
            </Col>
          </Row>
        )}
        {showSuggestions && (
          <Row>
            <Col lg={24}>
              <strong>Restricciones</strong>
              <Form.Item name={[field.name, 'suggestion']} initialValue={form.getFieldValue(diagnosticField)?.[field.name]?.restrictions} noStyle>
                <Input.TextArea bordered={false} placeholder={t('medicalConsultations.healthForm.suggestions')} autoSize={{ minRows: 1, maxRows: 5 }} />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Col>
    </Row>
  );
};

const Diagnostics = ({
  form,
  formData,
  recommendatiosModal,
  inputData,
  showDiagnosticTypification = false,
  diagnosticTypificationOptions = null,
  showIndications = true,
  showSuggestions = false,
}) => {
  const { t } = useTranslation();
  const diagnostic = useDiagnostics({
    defaultDiagnosticCodes: inputData ? inputData.default_value?.split(',') || [] : formData?.extra_data?.diagnostics || [],
  });
  const diagnosticField = inputData ? inputData.id : 'diagnostics';

  useEffect(() => {
    if (inputData && !form.getFieldValue(inputData.id) && formData?.status === 'created') {
      form.setFieldsValue({
        [inputData.id]: diagnostic.defaultDiagnostics,
      });
    } else if (!formData?.diagnostics && formData?.status === 'created') {
      form.setFieldsValue({
        diagnostics: diagnostic.defaultDiagnostics,
      });
    }
  }, [form, diagnostic.defaultDiagnostics, inputData]);

  return (
    <Row>
      <Col lg={24}>
        <DiagnosticSelector
          form={form}
          diagnosticField={diagnosticField}
          placeholder="Buscar diagnóstico..."
          showDiagnosticTypification={showDiagnosticTypification}
          diagnosticTypificationOptions={diagnosticTypificationOptions}
        />
      </Col>
      <Col lg={24}>
        <Form.List name={diagnosticField}>
          {(fields, { remove }) => (
            <div>
              {fields.length > 0 ? (
                <React.Fragment>
                  <Row gutter={[20, 20]} className="mt-20">
                    {showDiagnosticTypification && diagnosticTypificationOptions && (
                      <Col lg={2}>
                        <strong>{'Habilitar'}</strong>
                      </Col>
                    )}
                    <Col lg={showDiagnosticTypification ? 2 : 4}>
                      <strong>Código</strong>
                    </Col>
                    <Col lg={12}>
                      <strong>Descripción</strong>
                    </Col>
                  </Row>
                  {fields.map((field, index) => (
                    <DiagnosticCard
                      key={field.key}
                      field={field}
                      index={index}
                      onRemove={remove}
                      recommendatiosModal={recommendatiosModal}
                      diagnosticField={diagnosticField}
                      showDiagnosticTypification={showDiagnosticTypification}
                      diagnosticTypificationOptions={diagnosticTypificationOptions}
                      t={t}
                      form={form}
                      showIndications={showIndications}
                      showSuggestions={showSuggestions}
                    />
                  ))}
                </React.Fragment>
              ) : (
                <Empty description="No hay diagnósticos agregados" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          )}
        </Form.List>
      </Col>
    </Row>
  );
};

export default Diagnostics;
